import { Component, isDevMode, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { environment } from '../../../../../environments/environment';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoadingService } from '../../../../services/loading.service';
import { PermissionType } from '../../../../components/employee/PermissionType.enum';
import { CentreService } from '../../../../services/centre.service';
import firebase from 'firebase/compat/app';
import { FinancialYearService } from '../../../../services/financial-year.service';

interface PresentationData {
  id?: string;
  selectedCentre: string;
  selectedCentres?: string[];   // new multi-centre format
  centreName: string;
  startDate: firebase.firestore.Timestamp;
  endDate: firebase.firestore.Timestamp;
  componentVisibility: any[];
  goodTouchBadTouch: string;
  selectedLearningConcepts?: string[];
  teachingPlanParents: string;
  teachingPlanChildren: string;
  remarks: string;
  galleryImages?: any[];
  createdAt: firebase.firestore.Timestamp;
  createdBy: string;
  lastModified: firebase.firestore.Timestamp;
}

@Component({
  selector: 'app-education-presentation-list',
  templateUrl: './education-presentation-list.component.html',
  styleUrls: ['./education-presentation-list.component.scss']
})
export class EducationPresentationListComponent implements OnInit, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private _destroyed$ = new Subject<void>();
  displayedColumns: string[] = [
    'no',
    'centreName',
    'dateRange',
    'createdBy',
    'createdAt',
    'actions'
  ];

  dataSource = new MatTableDataSource<PresentationData>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  presentations$: Observable<PresentationData[]>;
  searchText: string = '';

  // Filters
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  filterCentre: string = '';
  centres$: Observable<any[]>;

  // Expose PermissionType to template
  readonly PermissionType = PermissionType;

  constructor(
    private firestore: AngularFirestore,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    public loadingService: LoadingService,
    private centreService: CentreService,
    private financialYearService: FinancialYearService
  ) {
    this.presentations$ = this.firestore.collection<PresentationData>(this.env.educationPresentationList,
      ref => ref.orderBy('createdAt', 'desc')
    ).valueChanges({ idField: 'id' });

    this.centres$ = this.centreService.getAllCentres();
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.filterStartDate = fy.startDate;
    this.filterEndDate = fy.endDate;
    this.loadPresentations();
  }


  loadPresentations(): void {
    this.loadingService.setLoading(true);
    this.presentations$.pipe(takeUntil(this._destroyed$)).subscribe(
      (data) => {
        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loadingService.setLoading(false);
      },
      (error) => {
        console.error('Error loading presentations:', error);
        this.snackBar.open('Error loading presentations', 'Close', { duration: 3000 });
        this.loadingService.setLoading(false);
      }
    );
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyAdvancedFilter(): void {
    this.loadingService.setLoading(true);

    let query = this.firestore.collection<PresentationData>(this.env.educationPresentationList, ref => {
      let queryRef: any = ref.orderBy('createdAt', 'desc');

      // Apply centre filter
      if (this.filterCentre && this.filterCentre !== '') {
        queryRef = queryRef.where('centreName', '==', this.filterCentre);
      }

      // Apply date range filter
      if (this.filterStartDate) {
        queryRef = queryRef.where('startDate', '>=', firebase.firestore.Timestamp.fromDate(this.filterStartDate));
      }

      if (this.filterEndDate) {
        queryRef = queryRef.where('startDate', '<=', firebase.firestore.Timestamp.fromDate(this.filterEndDate));
      }

      return queryRef;
    });

    query.valueChanges({ idField: 'id' }).pipe(takeUntil(this._destroyed$)).subscribe(
      (data) => {
        this.dataSource.data = data;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loadingService.setLoading(false);
        this.snackBar.open(`Found ${data.length} presentations`, 'Close', { duration: 2000 });
      },
      (error) => {
        console.error('Error filtering presentations:', error);
        this.snackBar.open('Error filtering presentations', 'Close', { duration: 3000 });
        this.loadingService.setLoading(false);
      }
    );
  }

  clearFilters(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.filterStartDate = fy.startDate;
    this.filterEndDate = fy.endDate;
    this.filterCentre = '';
    this.searchText = '';
    this.loadPresentations();
    this.snackBar.open('Filters cleared', 'Close', { duration: 2000 });
  }

  formatDate(timestamp: firebase.firestore.Timestamp): string {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatDateRange(startDate: firebase.firestore.Timestamp, endDate: firebase.firestore.Timestamp): string {
    return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
  }

  viewPresentation(presentation: PresentationData): void {
    // Navigate to education-presentation with query params to load data
    this.router.navigate(['/home/reports/education-presentation'], {
      queryParams: { id: presentation.id }
    });
  }

  editPresentation(presentation: PresentationData): void {
    // Navigate to education-presentation with query params to edit
    this.router.navigate(['/home/reports/education-presentation'], {
      queryParams: { id: presentation.id, mode: 'edit' }
    });
  }

  deletePresentation(presentation: PresentationData): void {
    const confirmDelete = confirm(
      `Are you sure you want to delete the presentation for "${presentation.centreName}"?\nDate Range: ${this.formatDateRange(presentation.startDate, presentation.endDate)}`
    );

    if (confirmDelete) {
      this.loadingService.setLoading(true);
      this.firestore.collection(this.env.educationPresentationList)
        .doc(presentation.id)
        .delete()
        .then(() => {
          this.snackBar.open('Presentation deleted successfully', 'Close', { duration: 3000 });
          this.loadingService.setLoading(false);
        })
        .catch((error) => {
          console.error('Error deleting presentation:', error);
          this.snackBar.open('Error deleting presentation: ' + error.message, 'Close', { duration: 5000 });
          this.loadingService.setLoading(false);
        });
    }
  }

  duplicatePresentation(presentation: PresentationData): void {
    this.loadingService.setLoading(true);

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');

    // Create a copy without the ID and update metadata
    const duplicateData = {
      ...presentation,
      createdAt: firebase.firestore.Timestamp.now(),
      createdBy: userData?.userName || 'Unknown',
      lastModified: firebase.firestore.Timestamp.now()
    };

    // Remove the id field
    delete duplicateData.id;

    this.firestore.collection(this.env.educationPresentationList)
      .add(duplicateData)
      .then((docRef) => {
        this.snackBar.open(`Presentation duplicated successfully! ID: ${docRef.id}`, 'Close', { duration: 5000 });
        this.loadingService.setLoading(false);
      })
      .catch((error) => {
        console.error('Error duplicating presentation:', error);
        this.snackBar.open('Error duplicating presentation: ' + error.message, 'Close', { duration: 5000 });
        this.loadingService.setLoading(false);
      });
  }

  exportToExcel(presentation: PresentationData): void {
    // TODO: Implement Excel export functionality
    this.snackBar.open('Excel export feature coming soon', 'Close', { duration: 3000 });
  }

  createNew(): void {
    this.router.navigate(['/home/reports/education-presentation']);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}

